import * as React from "react";
import { CheckIcon, PlusIcon, XIcon } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export type ChipsInputProps = {
  items: string[];
  onAdd: (item: string) => void | Promise<void>;
  onRemove: (item: string) => void | Promise<void>;

  addLabel?: string;
  placeholder?: string;
  chipIcon?: React.ReactNode;
  inputIcon?: React.ReactNode;
  validate?: (value: string) => string | null;
  transform?: (value: string) => string;
  duplicateMessage?: string;
  maxItems?: number;
  maxItemsMessage?: string;
  isLoading?: boolean;
  className?: string;
};

/**
 * @example
 * import { useState } from "react"
 * import { MailIcon } from "lucide-react"
 * import { ChipsInput } from "@/components/ui/chips-input"
 *
 * const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
 *
 * export function CcEmails() {
 *   const [emails, setEmails] = useState<string[]>([])
 *   return (
 *     <ChipsInput
 *       items={emails}
 *       onAdd={(email) => setEmails((list) => [...list, email])}
 *       onRemove={(email) => setEmails((list) => list.filter((e) => e !== email))}
 *       addLabel="Add CC email"
 *       placeholder="someone@example.com"
 *       chipIcon={<MailIcon className="size-3" />}
 *       validate={(email) => (EMAIL.test(email) ? null : "Invalid email address")}
 *       duplicateMessage="Email already added"
 *     />
 *   )
 * }
 */
export function ChipsInput({
  items,
  onAdd,
  onRemove,
  addLabel = "Add",
  placeholder,
  chipIcon,
  inputIcon,
  validate,
  transform,
  duplicateMessage = "Already added",
  maxItems,
  maxItemsMessage,
  isLoading,
  className,
}: ChipsInputProps) {
  const [isAdding, setIsAdding] = React.useState(false);
  const [draft, setDraft] = React.useState("");

  const committingRef = React.useRef(false);
  const removingRef = React.useRef<Set<string>>(new Set());

  const resolvedInputIcon = inputIcon ?? chipIcon;

  const commit = async () => {
    if (committingRef.current) return;

    const raw = draft.trim();
    if (!raw) return;

    const value = (transform ? transform(raw) : raw).trim();
    if (!value) {
      setDraft("");
      return;
    }

    if (items.includes(value)) {
      toast.error(duplicateMessage);
      return;
    }

    if (maxItems !== undefined && items.length >= maxItems) {
      toast.error(maxItemsMessage ?? `Maximum of ${maxItems} items allowed`);
      return;
    }

    if (validate) {
      const error = validate(value);
      if (error) {
        toast.error(error);
        return;
      }
    }

    committingRef.current = true;
    try {
      await onAdd(value);
      setDraft("");
      setIsAdding(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add");
    } finally {
      committingRef.current = false;
    }
  };

  const cancel = () => {
    setDraft("");
    setIsAdding(false);
  };

  const handleRemove = async (item: string) => {
    if (removingRef.current.has(item)) return;
    removingRef.current.add(item);
    try {
      await onRemove(item);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove");
    } finally {
      removingRef.current.delete(item);
    }
  };

  // Preserve order, drop duplicates defensively so key collisions can't crash
  // reconciliation if the parent ever briefly holds a dupe.
  const uniqueItems = React.useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const item of items) {
      if (!seen.has(item)) {
        seen.add(item);
        out.push(item);
      }
    }
    return out;
  }, [items]);

  return (
    <div
      className={cn("flex flex-wrap gap-2 notranslate", className)}
      translate="no"
    >
      {!isLoading && uniqueItems?.map((item) => (
        <div className="flex flex-row items-center" key={item}>
          <Badge
            variant="secondary"
            className="relative overflow-visible group h-8 pr-0.5 pl-2 [&_svg]:mr-1 rounded-r-none bg-muted-foreground/10"
          >
            {chipIcon}
            <span>{item}</span>
          </Badge>
          <Button
            size="sm"
            variant="secondary"
            className="rounded-l-none text-muted-foreground [&_svg]:size-3.5 size-8 hover:text-foreground bg-muted-foreground/10"
            onClick={() => handleRemove(item)}
            aria-label={`Remove ${item}`}
          >
            <XIcon />
          </Button>
        </div>
      ))}

      {!isLoading && !isAdding && (
        <Button
          size="sm"
          variant="muted-ghost"
          className="border-dashed border animate-in fade-in-0 slide-in-from-left-2 duration-200 font-normal"
          onClick={() => setIsAdding(true)}
        >
          <PlusIcon /> <span>{addLabel}</span>
        </Button>
      )}

      {!isLoading && isAdding && (
        <div className="flex items-center space-x-2 animate-in fade-in-0 slide-in-from-right-2 duration-200">
          <div className="relative">
            <Input
              className={cn(
                "peer h-8 placeholder:text-sm",
                resolvedInputIcon && "ps-8",
                className
              )}
              placeholder={placeholder}
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancel();
                }
              }}
            />
            {resolvedInputIcon && (
              <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 inset-s-0 flex items-center justify-center ps-2.5 peer-disabled:opacity-50">
                {resolvedInputIcon}
              </div>
            )}
          </div>
          <Button
            size="icon"
            variant="success-ghost"
            className="shrink-0 size-8"
            onMouseDown={(e) => e.preventDefault()}
            onClick={commit}
            aria-label="Confirm"
          >
            <CheckIcon />
          </Button>
          <Button
            size="icon"
            variant="destructive-ghost"
            className="shrink-0 size-8"
            onMouseDown={(e) => e.preventDefault()}
            onClick={cancel}
            aria-label="Cancel"
          >
            <XIcon />
          </Button>
        </div>
      )}

      {isLoading && (
        <div className="flex flex-wrap items-center justify-start h-8 gap-2 w-full">
          <Skeleton className="w-20 h-8" />
          <Skeleton className="w-20 h-8" />
          <Skeleton className="w-20 h-8" />
          <Skeleton className="w-20 h-8" />
          <Skeleton className="w-20 h-8" />
        </div>
      )}
    </div>
  );
}